// apps/web/src/features/admin/automations/CheckConfigBody.tsx
import type { ChangeEvent } from 'react';
import type { WebhookAuthConfig } from '@capture/shared';
import { FormulaField } from '../form-config/shared/FormulaField';
import { RegexAssistPanel } from '../form-config/rules/RegexAssistPanel';
import { WebhookConnectionFields } from '../form-config/behaviors/WebhookConnectionFields';

/** P2b — a line-item group the row_check editor can target. */
export interface RowCheckGroupOption {
  groupId: string;
  name: string;
  columns: string[];
}

interface Props {
  effectKey: string;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Form field names — powers the {field} insert-token affordance (#13). */
  fieldNames?: string[];
  /** P2b — line-item groups on the form, powering the row_check rowGroup +
   *  highlightColumn selects. */
  lineItemGroups?: RowCheckGroupOption[];
}

const s = (v: unknown): string => (typeof v === 'string' ? v : '');
const n = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);
const parseLines = (text: string): string[] => text.split('\n').map((l) => l.trim()).filter(Boolean);

const COMPARE_OPS: Array<{ value: string; label: string }> = [
  { value: 'gt', label: '> greater than' },
  { value: 'gte', label: '≥ at least' },
  { value: 'lt', label: '< less than' },
  { value: 'lte', label: '≤ at most' },
  { value: 'eq', label: '= equals' },
  { value: 'ne', label: '≠ not equal' },
];

/** Per-check config inputs. Authors type params only; name/severity live on the drawer. */
export function CheckConfigBody({ effectKey, config, onChange, fieldNames = [], lineItemGroups = [] }: Props) {
  const patch = (p: Record<string, unknown>) => onChange({ ...config, ...p });

  const messageRow = (
    <div className="automation-editor__row">
      <span>Message</span>
      <input
        data-testid="check-message"
        type="text"
        value={s(config.message)}
        placeholder="Shown when the check fails — use {field} to insert a value"
        onChange={(e: ChangeEvent<HTMLInputElement>) => patch({ message: e.target.value })}
      />
      {fieldNames.length > 0 && (
        <select
          data-testid="check-message-insert-field"
          aria-label="Insert field token"
          value=""
          onChange={(e) => {
            const name = e.target.value;
            if (!name) return;
            patch({ message: `${s(config.message)}{${name}}` });
          }}
        >
          <option value="">Insert field…</option>
          {fieldNames.map((fn) => (
            <option key={fn} value={fn}>{`{${fn}}`}</option>
          ))}
        </select>
      )}
    </div>
  );

  if (effectKey === 'validate_check') {
    const rawVt = s(config.validationType) || 'required';
    // Fold legacy friendly min/max into the compare sub-type (min≡gte, max≡lte)
    // so the editor shows the truth normalize-validate.ts will store (#1).
    const vt = rawVt === 'min' || rawVt === 'max' ? 'compare' : rawVt;
    const op = s(config.op) || (rawVt === 'min' ? 'gte' : rawVt === 'max' ? 'lte' : 'gt');
    return (
      <div className="automation-check-config">
        <label className="automation-editor__row">
          <span>Rule</span>
          <select data-testid="check-validationType" value={vt} onChange={(e) => patch({ validationType: e.target.value })}>
            <option value="required">Required</option>
            <option value="regex">Matches pattern</option>
            <option value="compare">Compare value</option>
            <option value="minLength">Min length</option>
            <option value="maxLength">Max length</option>
            <option value="oneOf">One of</option>
            <option value="custom">Custom expression</option>
          </select>
        </label>
        {vt === 'regex' && (
          <>
            <label className="automation-editor__row">
              <span>Pattern</span>
              <input data-testid="check-pattern" type="text" value={s(config.pattern)} placeholder="^\\d{3}$"
                onChange={(e) => patch({ pattern: e.target.value })} />
            </label>
            <RegexAssistPanel pattern={s(config.pattern)} onAccept={(p) => patch({ pattern: p })} />
          </>
        )}
        {vt === 'compare' && (
          <>
            <label className="automation-editor__row">
              <span>Operator</span>
              <select data-testid="check-compare-op" value={op}
                onChange={(e) => patch({ validationType: 'compare', op: e.target.value })}>
                {COMPARE_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="automation-editor__row">
              <span>Value</span>
              <input data-testid="check-compare-value" type="number" value={n(config.value) ?? ''}
                onChange={(e) => patch({ validationType: 'compare', op, value: e.target.value === '' ? undefined : Number(e.target.value) })} />
            </label>
          </>
        )}
        {(vt === 'minLength' || vt === 'maxLength') && (
          <label className="automation-editor__row">
            <span>{vt === 'minLength' ? 'Minimum' : 'Maximum'} length</span>
            <input data-testid="check-length-value" type="number" min={0} value={n(config.value) ?? ''}
              onChange={(e) => patch({ value: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </label>
        )}
        {vt === 'oneOf' && (
          <label className="automation-editor__row">
            <span>Allowed values</span>
            <textarea data-testid="check-oneof-values" placeholder="One value per line"
              value={Array.isArray(config.values) ? (config.values as string[]).join('\n') : ''}
              onChange={(e) => patch({ validationType: 'oneOf', values: parseLines(e.target.value) })} />
          </label>
        )}
        {vt === 'custom' && (
          <div className="automation-editor__row">
            <span>Expression</span>
            <FormulaField
              value={{ source: s(config.expression) }}
              onChange={(next) => patch({ validationType: 'custom', expression: next.source })}
              fieldNames={fieldNames}
              context="rule"
              ariaLabel="Validation expression"
            />
          </div>
        )}
        {messageRow}
      </div>
    );
  }

  if (effectKey === 'unique_check') {
    // P5 T5 — composite uniqueness: additional member fields make the check
    // "unique per <scope> on host + these"; withinDays bounds the comparison
    // to a recent createdAt window (blank = all time).
    const memberFields = Array.isArray(config.fields) ? (config.fields as string[]) : [];
    const toggleMember = (name: string) => {
      const next = memberFields.includes(name)
        ? memberFields.filter((f) => f !== name)
        : [...memberFields, name];
      patch({ fields: next });
    };
    return (
      <div className="automation-check-config">
        <label className="automation-editor__row">
          <span>Unique within</span>
          <select data-testid="check-unique-scope" value={s(config.scope) || 'batch'}
            onChange={(e) => patch({ scope: e.target.value })}>
            <option value="batch">This batch</option>
            <option value="tenant">The whole tenant</option>
          </select>
        </label>
        {fieldNames.length > 0 && (
          <div className="automation-editor__row">
            <span>Also match on</span>
            <div className="automation-check-config__members">
              {fieldNames.map((fn) => (
                <label key={fn} className="automation-check-config__member">
                  <input
                    type="checkbox"
                    data-testid={`check-unique-field-${fn}`}
                    checked={memberFields.includes(fn)}
                    onChange={() => toggleMember(fn)}
                  />
                  <span>{fn}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        <label className="automation-editor__row">
          <span>Within (days)</span>
          <input data-testid="check-unique-within-days" type="number" min={1}
            value={n(config.withinDays) ?? ''}
            onChange={(e) => patch({ withinDays: e.target.value === '' ? undefined : Number(e.target.value) })} />
        </label>
        {messageRow}
      </div>
    );
  }

  if (effectKey === 'historical_range_check') {
    return (
      <div className="automation-check-config">
        <label className="automation-editor__row">
          <span>Std. deviations</span>
          <input data-testid="check-stddev" type="number" min={1} value={n(config.stdDeviations) ?? 3}
            onChange={(e) => patch({ stdDeviations: e.target.value === '' ? undefined : Number(e.target.value) })} />
        </label>
        {messageRow}
      </div>
    );
  }

  if (effectKey === 'webhook_check') {
    // Reuse the WU-3.2 shared connection sub-form (url + auth none/bearer/basic/
    // api_key + editable timeoutMs). Its keys (url/auth/timeoutMs) pass through
    // makeConfigCheck's `config = rest` and are read verbatim by the runtime
    // webhook-evaluator/-caller. failMode + message stay Capture-specific here.
    return (
      <div className="automation-check-config">
        <WebhookConnectionFields
          value={{ url: s(config.url), auth: config.auth as WebhookAuthConfig | undefined, timeoutMs: n(config.timeoutMs) }}
          onChange={(next) => patch({ url: next.url, auth: next.auth, timeoutMs: next.timeoutMs })}
        />
        <label className="automation-editor__row">
          <span>On failure</span>
          <select data-testid="check-webhook-failmode" value={s(config.failMode) || 'soft'}
            onChange={(e) => patch({ failMode: e.target.value })}>
            <option value="soft">Warn (soft)</option>
            <option value="hard">Block (hard)</option>
          </select>
        </label>
        {messageRow}
      </div>
    );
  }

  if (effectKey === 'row_check') {
    // The condition (edited in the branch's IF) carries the per-row invariant;
    // here the author picks WHICH line-item group is checked and WHICH cell
    // carries the failure outline. highlightColumn is optional (defaults to the
    // first group column the condition references).
    const rowGroup = s(config.rowGroup);
    const selectedGroup = lineItemGroups.find((g) => g.groupId === rowGroup);
    const columns = selectedGroup?.columns ?? [];
    return (
      <div className="automation-check-config">
        <label className="automation-editor__row">
          <span>Check each row of</span>
          <select
            data-testid="check-rowgroup"
            value={rowGroup}
            onChange={(e) => patch({ rowGroup: e.target.value, highlightColumn: undefined })}
          >
            <option value="">Select a line-item group…</option>
            {lineItemGroups.map((g) => (
              <option key={g.groupId} value={g.groupId}>{g.name}</option>
            ))}
          </select>
        </label>
        <label className="automation-editor__row">
          <span>Flag the cell</span>
          <select
            data-testid="check-highlight-column"
            value={s(config.highlightColumn)}
            disabled={columns.length === 0}
            onChange={(e) => patch({ highlightColumn: e.target.value || undefined })}
          >
            <option value="">First referenced column (auto)</option>
            {columns.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        {messageRow}
      </div>
    );
  }

  // cross_field_check — condition carries the logic; only a message here.
  return <div className="automation-check-config">{messageRow}</div>;
}
